import { io, Socket } from 'socket.io-client';
import { watch, type WatchStopHandle } from 'vue';
import { useAuthStore } from '../stores/auth';
import { useNotificationsStore } from '../stores/notifications';
import { refreshSession } from './api';

let socket: Socket | null = null;

/** Refresh-and-retry rounds after a failed authentication, per connection (a dead session must not loop). */
export const SOCKET_AUTH_RETRIES = 2;
/** This connection answered `authenticated { success: true }`. */
let socketAuthed = false;
let authRetries = 0;
let recoveringAuth = false;
let stopTokenWatch: WatchStopHandle | null = null;

/** The access token right now (the store mirrors every refresh; localStorage before the store exists). */
function currentAccessToken(): string {
  return useAuthStore().accessToken ?? localStorage.getItem('accessToken') ?? '';
}

/**
 * `authenticated { success: false }`: TOKEN_EXPIRED, INVALID_TOKEN or an
 * older server's code-less failure → one shared refresh, then authenticate
 * again with the new token. ACCOUNT_INACTIVE ends the session elsewhere
 * (the API answers 401 ACCOUNT_INACTIVE), so it is not retried.
 */
async function recoverSocketAuth(code: string | undefined): Promise<void> {
  if (code === 'ACCOUNT_INACTIVE' || recoveringAuth || authRetries >= SOCKET_AUTH_RETRIES) return;
  authRetries += 1;
  recoveringAuth = true;
  try {
    const token = await refreshSession();
    if (socket?.connected && !socketAuthed && token) socket.emit(SOCKET_EVENTS.AUTHENTICATE, { token });
  } catch {
    // Definitive: the API layer signed out. Transient: the next token
    // change or reconnection authenticates again.
  } finally {
    recoveringAuth = false;
  }
}

/** event → callbacks attached to every socket instance (see onPersistentSocketEvent). */
const persistentListeners = new Map<string, Set<(data: unknown) => void>>();

export const SOCKET_EVENTS = {
  // Connection
  CONNECT: 'connection',
  DISCONNECT: 'disconnect',

  // Authentication
  AUTHENTICATE: 'authenticate',
  AUTHENTICATED: 'authenticated',

  // Attendance
  ATTENDANCE_CLOCK_IN: 'attendance:clock-in',
  ATTENDANCE_CLOCK_OUT: 'attendance:clock-out',

  // Tasks
  TASK_CREATED: 'task:created',
  TASK_UPDATED: 'task:updated',
  TASK_DELETED: 'task:deleted',
  TASK_STATUS_CHANGED: 'task:status-changed',
  TASK_COMMENT_ADDED: 'task:comment-added',

  // Projects
  PROJECT_CREATED: 'project:created',
  PROJECT_UPDATED: 'project:updated',
  PROJECT_MEMBER_ADDED: 'project:member-added',

  // Timer
  TIMER_STARTED: 'timer:started',
  TIMER_STOPPED: 'timer:stopped',
  TIMER_PAUSED: 'timer:paused',

  // Notifications
  NOTIFICATION_NEW: 'notification:new',
  NOTIFICATION_READ: 'notification:read',

  // Messages
  MESSAGE_NEW: 'message:new',
  MESSAGE_READ: 'message:read',
  MESSAGE_TYPING: 'message:typing',
  MESSAGE_STOP_TYPING: 'message:stop-typing',

  // Presence
  USER_ONLINE: 'user:online',
  USER_OFFLINE: 'user:offline',
  PRESENCE_REQUEST: 'presence:request',
  PRESENCE_RESPONSE: 'presence:response',

  // Leaves
  LEAVE_APPLIED: 'leave:applied',
  LEAVE_APPROVED: 'leave:approved',
  LEAVE_REJECTED: 'leave:rejected',

  // Documents (TDoc / TSheet collaboration)
  DOCUMENT_JOIN: 'document:join',
  DOCUMENT_LEAVE: 'document:leave',
  DOCUMENT_PRESENCE: 'document:presence',
  DOCUMENT_UPDATED: 'document:updated',

  // AI Agent
  AI_MESSAGE_STREAM: 'ai:message-stream',
  AI_GENERATION_COMPLETE: 'ai:generation-complete',
  AI_CREDITS_LOW: 'ai:credits-low',

  // Pins (topbar "Pinned" control — the server emits to the pin owner after every change)
  PINS_CHANGED: 'pins:changed',

  // Project roadmap (ids only, company room; clients refetch through the API)
  ROADMAP_CHANGED: 'roadmap:changed',

  // RBAC (room user:{id}; rbac/refresh.ts): refetch /auth/permissions, or sign out
  PERMISSIONS_CHANGED: 'permissions:changed',
  SESSION_REVOKED: 'session:revoked',

  // General
  JOIN_ROOM: 'join-room',
  LEAVE_ROOM: 'leave-room',
} as const;

export function initSocket(): Socket {
  if (socket) return socket;

  const socketUrl = import.meta.env.VITE_SOCKET_URL || window.location.origin;

  socket = io(socketUrl, {
    autoConnect: false,
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    timeout: 20000,
    // Read at every (re)connection: a reconnect after a token refresh carries
    // the fresh token, so the server authenticates the socket at once.
    auth: (cb) => cb({ token: currentAccessToken() }),
  });

  // 'connect' fires for every reconnection too (socket.io v4's 'reconnect'
  // is a Manager event, never emitted on the socket), so this covers both.
  socket.on('connect', () => {
    console.log('[Socket] Connected');
    socketAuthed = false;
    authRetries = 0;
    authenticateSocket(); // servers that predate handshake authentication
  });

  socket.on('disconnect', (reason) => {
    socketAuthed = false;
    console.log('[Socket] Disconnected:', reason);
    // If server closed the connection, reconnect immediately
    if (reason === 'io server disconnect' || reason === 'transport close' || reason === 'transport error') {
      setTimeout(() => socket?.connect(), 500);
    }
  });

  socket.on('connect_error', (err) => {
    console.warn('[Socket] Connection error:', err.message);
  });

  socket.on(SOCKET_EVENTS.AUTHENTICATED, (data: { success: boolean; code?: string; error?: string }) => {
    if (data?.success) {
      socketAuthed = true;
      console.log('Socket authenticated');
      return;
    }
    console.error('Socket authentication failed:', data?.code ?? data?.error);
    // After a success the server keeps the identity; only an unauthenticated socket needs a fresh token.
    if (!socketAuthed) void recoverSocketAuth(data?.code);
  });

  // A token refreshed by an API call reaches a socket still waiting for one.
  stopTokenWatch = watch(
    () => useAuthStore().accessToken,
    (token) => {
      if (token && socket?.connected && !socketAuthed && !recoveringAuth) authenticateSocket();
    }
  );

  // Handle new notifications
  socket.on(SOCKET_EVENTS.NOTIFICATION_NEW, (notification) => {
    const notificationsStore = useNotificationsStore();
    notificationsStore.addNotification(notification);
  });

  // Listeners registered before the socket existed (onPersistentSocketEvent)
  const created = socket;
  persistentListeners.forEach((callbacks, event) => callbacks.forEach((cb) => created.on(event, cb)));

  return socket;
}

/**
 * Listeners that are attached whenever the socket exists — also when they
 * are registered before the first connectSocket() (rbac/refresh.ts starts at
 * boot, the layout connects later). Returns an unsubscribe function.
 */
export function onPersistentSocketEvent<T>(event: string, callback: (data: T) => void): () => void {
  const cb = callback as (data: unknown) => void;
  let callbacks = persistentListeners.get(event);
  if (!callbacks) {
    callbacks = new Set();
    persistentListeners.set(event, callbacks);
  }
  callbacks.add(cb);
  socket?.on(event, cb);
  return () => {
    persistentListeners.get(event)?.delete(cb);
    socket?.off(event, cb);
  };
}

export function connectSocket(): void {
  if (!socket) {
    initSocket();
  }
  socket?.connect();
}

export function disconnectSocket(): void {
  socket?.disconnect();
}

export function authenticateSocket(): void {
  const authStore = useAuthStore();
  if (socket && authStore.accessToken) {
    socket.emit(SOCKET_EVENTS.AUTHENTICATE, { token: authStore.accessToken });
  }
}

export function joinRoom(room: string): void {
  socket?.emit(SOCKET_EVENTS.JOIN_ROOM, room);
}

export function leaveRoom(room: string): void {
  socket?.emit(SOCKET_EVENTS.LEAVE_ROOM, room);
}

export function onSocketEvent<T>(event: string, callback: (data: T) => void): void {
  socket?.on(event, callback);
}

export function offSocketEvent(event: string, callback?: (...args: unknown[]) => void): void {
  if (callback) {
    socket?.off(event, callback);
  } else {
    socket?.off(event);
  }
}

export function getSocket(): Socket | null {
  return socket;
}

/** Test helper: forget the socket instance and its authentication state. */
export function resetSocketForTests(): void {
  stopTokenWatch?.();
  stopTokenWatch = null;
  socket = null;
  socketAuthed = false;
  authRetries = 0;
  recoveringAuth = false;
}

export default {
  initSocket,
  connectSocket,
  disconnectSocket,
  authenticateSocket,
  joinRoom,
  leaveRoom,
  onSocketEvent,
  offSocketEvent,
  getSocket,
  SOCKET_EVENTS,
};
